// @ts-nocheck -- Transitional typing for a legacy JavaScript module.
import { useMemo, useState } from "react";
import { useNavigate, useParams, useSearchParams, Link } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import {
  useGetDeal,
  useGetDealStats,
  useGetTasks,
  useGetDocuments,
  useGetActivity,
  usePatchTask,
  useNudgeTask,
  usePostTask,
  usePostDocument,
  usePatchDocument,
  useReorderTasks,
  useDeleteTask,
  usePostParticipant,
  usePatchDeal,
  useArchiveDeal,
  useCancelDeal,
} from "@/hooks/useDeals";
import { useGetAsks, usePostAskReply, usePatchCloseAsk, usePostDealUpdate } from "@/hooks/useRatingsAsks";
import { useDebouncedValue } from "@/hooks/useDebouncedValue";
import { usePostResendInvitation } from "@/hooks/useInvitations";
import { useBillingGate } from "@/hooks/useBillingGate";
import { useDocumentViewer } from "@/components/deals/documents/DocumentViewer";
import { CONTINGENCY_ANCHOR_TITLE, DEAL_TABS, PHASE_LABELS, ROLE_STYLES } from "@/constant/deals.constant";
import { Archive, ArrowRight, ChevronLeft, XCircle } from "lucide-react";
import ShareTab from "@/components/deals/tabs/ShareTab";
import DealTabs from "@/components/deals/tabs/DealTabs";
import TimelineTab from "@/components/deals/tabs/TimelineTab";
import DocumentsTab from "@/components/deals/tabs/DocumentsTab";
import TeamTab from "@/components/deals/tabs/TeamTab";
import CommunicationTab from "@/components/deals/tabs/CommunicationTab";
import ActivityTab from "@/components/deals/tabs/ActivityTab";
import AppointmentsTab from "@/components/deals/tabs/AppointmentsTab";
import AiTab from "@/components/deals/tabs/AiTab";
import DealChat from "@/components/deals/chat/DealChat";
import { totalUnread, useChatThreads } from "@/hooks/useDealChat";
import { useChatSocketStatus } from "@/lib/chatSocket";
import ScheduleTaskModal from "@/components/deals/ScheduleTaskModal";
import TaskDialog from "@/components/deals/dialogs/TaskDialog";
import DocumentDialog from "@/components/deals/dialogs/DocumentDialog";
import ParticipantDialog from "@/components/deals/dialogs/ParticipantDialog";
import { DEFAULT_COUNTRY, toE164 } from "@/lib/phone";
import UpdateDialog from "@/components/deals/dialogs/UpdateDialog";
import { PROTECTED_ROUTES } from "@/routes/url";
import {
  createDocumentForm,
  findInspectionTask,
  isoToLocalDateTime,
  localDateTimeToIso,
  createParticipantForm,
  createTaskForm,
  createContingencyForm,
  getDealPhases,
  getTaskProgress,
  isScheduledTask,
  requestTypeToPayload,
  taskPhaseFilters,
} from "@/utils/deals.utils";
import DealDetailSkeleton from "@/components/deals/skeletons/DealDetailSkeleton";
import { buildStagePipeline } from "@/utils/deals-command.utils";
import DealIdentityHeader from "@/components/deals/DealIdentityHeader";
import DealOverview from "@/components/deals/DealOverview";
import { StagePipeline } from "@/components/deals/command/CommandCenter";
import SnapshotEditor from "@/components/deals/SnapshotEditor";
import ContractTermsPrompt from "@/components/deals/ContractTermsPrompt";
import DealAddressDialog from "@/components/deals/DealAddressDialog";
import ConfirmDialog from "@/components/common/ConfirmDialog";
import { formatRelativeTime } from "@/utils/common.utils";
import { DealPhase, TaskStatus } from "@/enum/common.enum";
import { toast } from "sonner";

export default function DealDetail() {
  const { id } = useParams();
  const { user, context } = useAuth();
  const canAct = Boolean(context?.membership);
  const { data: deal, refetch: refetchDeal } = useGetDeal(id);
  // Read at the page rather than inside the Chat tab, so the tab's unread
  // badge keeps counting while the agent works in Tasks or Documents.
  const chatSocketStatus = useChatSocketStatus();
  const { data: chatThreads } = useChatThreads(deal ? id : undefined, chatSocketStatus);
  const chatViewer = useMemo(
    () => (user ? { id: user.id, name: user.name, role: "agent", internal: true } : null),
    [user]
  );
  const [showContractTerms, setShowContractTerms] = useState(false);
  const navigate = useNavigate();
  const [endDeal, setEndDeal] = useState<null | "cancel" | "archive">(null);
  const { mutateAsync: cancelDeal } = useCancelDeal();
  const { mutateAsync: archiveDeal } = useArchiveDeal();
  const [showAddress, setShowAddress] = useState(false);
  const [showSnapshotEditor, setShowSnapshotEditor] = useState(false);
  const [showAdvanceConfirm, setShowAdvanceConfirm] = useState(false);
  const [taskSearch, setTaskSearch] = useState("");
  // `null` means "the agent hasn't chosen" — distinct from `""`, which is the
  // All chip. Until they choose, the timeline is gated to the phase the deal is
  // actually in, so a brand-new deal opens on the handful of things happening
  // now instead of the entire lifetime checklist. Deriving it here rather than
  // in an effect avoids a first paint showing everything and then collapsing.
  const [taskPhase, setTaskPhase] = useState<string | null>(null);
  // Command center is the default view. The spreadsheet is one click away and
  // keeps its own search + phase chips.
  const [taskView, setTaskView] = useState("command");
  // Default to the deal's current phase, but only if it's a phase that carries
  // tasks (pre_listing / listing / under_contract). A `closed` deal has no
  // closed-phase tasks, so defaulting to it would empty the list; fall back to
  // All there.
  const dealPhaseHasChip = deal
    ? taskPhaseFilters(deal).some((f) => f.value === deal.phase)
    : false;
  const effectiveTaskPhase = taskPhase ?? (dealPhaseHasChip ? deal.phase : "");
  // The timeline search and phase tabs run server-side. They used to filter
  // `tasks` in memory, which only ever saw the first page the endpoint
  // returned — so a search matched nothing beyond it, silently. Debounced so
  // typing is one request, not one per keystroke.
  const debouncedTaskSearch = useDebouncedValue(taskSearch.trim(), 300);
  // Both views scope to the deal's current phase by default (effectiveTaskPhase
  // falls back to deal.phase): under contract, an agent wants the under-contract
  // checklist, not later stages. Search stays table-only.
  const isCommandView = taskView === "command";
  const taskParams = useMemo(() => ({
    ...(!isCommandView && debouncedTaskSearch ? { q: debouncedTaskSearch } : {}),
    ...(effectiveTaskPhase ? { phase: effectiveTaskPhase } : {}),
    // One page covers any real deal's timeline; the list is a chronological
    // sequence, so splitting it across pages would break the ordering the
    // template exists to establish.
    limit: 500,
  }), [isCommandView, debouncedTaskSearch, effectiveTaskPhase]);
  const { data: tasksData, refetch: refetchTasks } = useGetTasks(id, { params: taskParams });
  // Deal Status reads the whole checklist, not the phase/search-filtered view
  // above — an overdue task from another phase still puts the deal at risk.
  // Shares the cache with the tab whenever that is showing All.
  const { data: allTasksData } = useGetTasks(id, { params: { limit: 500 } });
  const { data: docsData, refetch: refetchDocs } = useGetDocuments(id);
  const { data: activityData, refetch: refetchActivity } = useGetActivity(id);
  // Header-strip numbers. Computed server-side over the same visibility-filtered
  // task/document/activity sets the tabs below show, so the strip and the lists
  // can never disagree.
  const { data: dealStats } = useGetDealStats(id);
  const tasks = tasksData?.tasks ?? [];
  const allTasks = allTasksData?.tasks ?? [];
  const docs = docsData ?? [];
  const activity = activityData ?? [];
  const { mutate: patchTask, isPending: patchingTask } = usePatchTask();
  const { mutate: nudgeTask } = useNudgeTask();
  const { mutate: postTask, isPending: postingTask } = usePostTask();
  const { mutateAsync: postDocument } = usePostDocument();
  const { mutate: patchDocument } = usePatchDocument();
  const { mutate: postParticipant, isPending: invitingParticipant } = usePostParticipant();
  const { mutate: patchDeal, isPending: advancing } = usePatchDeal();
  const { mutate: patchDealTerms } = usePatchDeal();
  // Own instance so a cover save doesn't flip `advancing`; async so the modal can await it.
  const { mutateAsync: saveCover } = usePatchDeal();
  const { mutate: reorderTasks } = useReorderTasks();
  const { mutate: deleteTask } = useDeleteTask();
  // Inviting into a deal grants access, so it is gated exactly like every other
  // invite — the button says why instead of failing on submit.
  const { blocked: billingBlocked, blockedReason } = useBillingGate();
  const { mutate: resendInvitation, isPending: resendingInvite } = usePostResendInvitation();
  const { data: asksData } = useGetAsks(id);
  const asks = asksData ?? [];
  const { mutate: postAskReply } = usePostAskReply(id);
  const { mutate: postDealUpdate, isPending: sendingUpdate } = usePostDealUpdate(id);
  const { mutate: patchCloseAsk } = usePatchCloseAsk(id);
  const { openDocument: openDoc, viewer: documentViewer } = useDocumentViewer();
  // Notification emails deep-link to a tab (`?tab=voice`). Without this the
  // link opened the deal on Tasks with no hint why, which read as the message
  // having vanished. Unknown values fall through to the default.
  const [searchParams, setSearchParams] = useSearchParams();
  const requestedTab = searchParams.get("tab");
  // AI Studio is hidden entirely when the backend has no OpenAI key, rather
  // than left clickable to answer 503. The `?tab=ai` guard matters too: a
  // bookmarked or shared link would otherwise open an empty pane.
  // Brief 1.15: it only produces listing copy, so it is scoped to the phase
  // where that copy is written — pre-listing. Once the listing is live or the
  // deal is under contract the tab has nothing to offer.
  const aiPhase = deal?.phase === DealPhase.PRE_LISTING;
  const aiEnabled = context?.ai_enabled !== false && aiPhase;
  const visibleTabs = useMemo(
    () => DEAL_TABS.filter((item) => item.k !== "ai" || aiEnabled),
    [aiEnabled]
  );
  const [tab, setTab] = useState(() =>
    DEAL_TABS.some((item) => item.k === requestedTab) ? requestedTab : "overview");
  const activeTab = visibleTabs.some((item) => item.k === tab) ? tab : "overview";
  const changeTab = (next) => {
    setTab(next);
    // Keep the URL honest so a refresh or a shared link lands in the same place.
    setSearchParams((current) => {
      const params = new URLSearchParams(current);
      params.set("tab", next);
      return params;
    }, { replace: true });
  };
  const [showTask, setShowTask] = useState(false);
  const [taskForm, setTaskForm] = useState(createTaskForm);
  const [showDoc, setShowDoc] = useState(false);
  const [documentForm, setDocumentForm] = useState(createDocumentForm);
  const [documentRequestedFor, setDocumentRequestedFor] = useState(null);
  const [scheduleTask, setScheduleTask] = useState(null);   // task currently being scheduled
  const [showParticipant, setShowParticipant] = useState(false);
  const [openAskId, setOpenAskId] = useState(null);
  const [askReply, setAskReply] = useState("");
  const [showUpdate, setShowUpdate] = useState(false);
  const [clientActionTaskId, setClientActionTaskId] = useState(null);

  const sendAskReply = (ask) => {
    if (!askReply.trim()) return;
    postAskReply(
      { askId: ask.id, payload: { body: askReply } },
      { onSuccess: () => setAskReply("") }
    );
  };

  const closeAsk = (ask) => {
    patchCloseAsk(ask.id);
  };

  const sendUpdate = (values) => {
    postDealUpdate(values, { onSuccess: () => setShowUpdate(false) });
  };

  // Phase only ever moves forward, one step at a time along the deal's own
  // pathway. `usePatchDeal` invalidates the detail + list queries, so the
  // header, countdowns and board all re-read the new phase on success.
  const advancePhase = () => {
    const order = getDealPhases(deal);
    const next = order[order.indexOf(deal.phase) + 1];
    if (!next) return;
    patchDeal(
      { dealId: id, payload: { phase: next } },
      {
        // Same offer the board makes when a card is dropped into Under contract.
        // Only after the move sticks, and never blocking it.
        onSuccess: () => { if (next === DealPhase.UNDER_CONTRACT) setShowContractTerms(true); },
      }
    );
  };

  const confirmAdvancePhase = () => setShowAdvanceConfirm(true);

  const toggleTask = (t) => {
    // Marking a "scheduled" task complete → capture the real-world date/time first.
    if (t.status !== TaskStatus.COMPLETE && isScheduledTask(t) && !t.scheduled_at) {
      setScheduleTask(t);
      return;
    }
    const next = t.status === TaskStatus.COMPLETE ? TaskStatus.PENDING : TaskStatus.COMPLETE;
    patchTask(
      { taskId: t.id, dealId: id, payload: { status: next } },
      {
        // Ticking "Offer accepted" moves the deal under contract on the server,
        // so offer the same contract-terms prompt the Advance button does.
        onSuccess: () => {
          if (next === TaskStatus.COMPLETE && t.advances_phase_to === DealPhase.UNDER_CONTRACT) setShowContractTerms(true);
        },
      }
    );
  };

  const releaseTaskToClient = (task) => {
    setClientActionTaskId(task.id);
    patchTask(
      { taskId: task.id, dealId: id, payload: { released_at: new Date().toISOString() } },
      {
        onSuccess: () => toast.success(`“${task.title}” was sent to the client.`),
        onSettled: () => setClientActionTaskId(null),
      }
    );
  };

  const confirmTaskForClient = (task) => {
    setClientActionTaskId(task.id);
    patchTask(
      { taskId: task.id, dealId: id, payload: { confirmed_at: new Date().toISOString() } },
      {
        onSuccess: () => toast.success(`“${task.title}” was confirmed and shared with the client.`),
        onSettled: () => setClientActionTaskId(null),
      }
    );
  };

  const saveScheduled = ({ scheduled_at, scheduled_notes }) => {
    if (!scheduleTask) return;
    patchTask(
      {
        taskId: scheduleTask.id,
        dealId: id,
        payload: { status: TaskStatus.COMPLETE, scheduled_at, scheduled_notes: scheduled_notes || "" },
      },
      {
        onSuccess: () => {
          syncChecklistToInspection(scheduleTask, scheduled_at);
          setScheduleTask(null);
        },
      }
    );
  };

  // Contract Details and the checklist describe the same inspection. Saving the
  // inspection date in either place fills in the other, so the agent never
  // enters it twice and the task does not sit open after it is booked. Done
  // here rather than on the server because only the browser knows the agent's
  // time zone, and the checklist stores the booking as a UTC instant.
  const syncInspectionToChecklist = (fresh) => {
    refetchDeal();
    const terms = fresh?.contract_terms || {};
    const task = findInspectionTask(allTasks, deal);
    const scheduled_at = localDateTimeToIso(terms.inspection_date, terms.inspection_start_time);
    if (!task || !scheduled_at) return;
    if (task.status === TaskStatus.COMPLETE && task.scheduled_at === scheduled_at) return;
    patchTask({ taskId: task.id, dealId: id, payload: { status: TaskStatus.COMPLETE, scheduled_at } });
  };

  const syncChecklistToInspection = (task, scheduled_at) => {
    if (!scheduled_at || findInspectionTask(allTasks, deal)?.id !== task.id) return;
    const { date, time } = isoToLocalDateTime(scheduled_at);
    const terms = deal?.contract_terms || {};
    if (terms.inspection_date === date && terms.inspection_start_time === time) return;
    patchDealTerms({ dealId: id, payload: { contract_terms: { inspection_date: date, inspection_start_time: time } } });
  };

  const skipScheduled = () => {
    if (!scheduleTask) return;
    patchTask(
      { taskId: scheduleTask.id, dealId: id, payload: { status: TaskStatus.COMPLETE } },
      { onSuccess: () => setScheduleTask(null) }
    );
  };

  const editTask = (task) => {
    setTaskForm({
      id: task.id,
      title: task.title || "",
      description: task.description || "",
      assigned_role: task.assigned_role || "agent",
      phase: task.phase,
      due_date: task.due_date || "",
      due_date_source: task.due_date_source === "relative_to_closing" ? "relative_to_closing" : "manual",
      due_date_offset_days: task.due_date_offset_days ?? 3,
      priority: task.priority || "medium",
      audience: task.audience || "client",
      client_release: task.client_release || "auto",
      request_type: task.request_type || "none",
      after_task_id: undefined,
      waived_at: task.waived_at || null,
    });
    setShowTask(true);
  };

  const closeTaskDialog = () => {
    setShowTask(false);
    setTaskForm(createTaskForm(deal.phase));
  };

  // "Not needed" keeps the row (and its history) but takes it off the client's
  // checklist and out of progress; "Needed after all" brings it back.
  const toggleTaskNeeded = (task, notNeeded) => {
    patchTask(
      { taskId: task.id, dealId: id, payload: { waived_at: notNeeded ? new Date().toISOString() : null } },
      { onSuccess: () => { closeTaskDialog(); toast.success(notNeeded ? "Marked not needed" : "Back on the checklist"); } },
    );
  };

  const removeTask = (task) => {
    deleteTask({ taskId: task.id, dealId: id }, { onSuccess: closeTaskDialog });
  };

  const normalizeTaskPayload = (values) => {
    const payload = { ...values };
    // Same rule as the dialog's picker: only a client task the client can see.
    const canAskClient = payload.assigned_role === "client" && payload.audience === "client";
    payload.request_type = canAskClient ? requestTypeToPayload(payload.request_type) : null;
    if (payload.due_date_source === "relative_to_closing") {
      payload.due_date = null;
      payload.due_date_offset_days = Number(payload.due_date_offset_days ?? 3);
      return payload;
    }
    payload.due_date_offset_days = null;
    payload.due_date_source = payload.due_date ? "manual" : "template";
    return payload;
  };

  const submitTask = (payload) => {
    const normalized = normalizeTaskPayload(payload);
    const onSuccess = () => {
      setShowTask(false);
      setTaskForm(createTaskForm(deal.phase));
    };
    if (taskForm.id) {
      const { id: _id, after_task_id: _a, ...rest } = normalized;
      patchTask({ taskId: taskForm.id, dealId: id, payload: rest }, {
        onSuccess: () => { onSuccess(); toast.success("Task updated"); },
      });
    } else {
      postTask({ dealId: id, payload: normalized }, { onSuccess });
    }
  };

  const addDoc = async (values) => {
    await postDocument({ dealId: id, payload: values });
    setShowDoc(false);
    setDocumentForm(createDocumentForm());
    setDocumentRequestedFor(null);
  };

  const openDocumentDialog = () => {
    setDocumentForm(createDocumentForm());
    setDocumentRequestedFor(null);
    setShowDoc(true);
  };

  const addParticipant = (values) => {
    // `phone_country` is form-local: it exists to read `phone` against, and the
    // server only ever stores E.164. Destructured out rather than deleted so it
    // cannot reach the request body.
    const { send_sms, phone_country, phone, ...rest } = values;
    postParticipant(
      {
        dealId: id,
        payload: {
          ...rest,
          phone: phone ? toE164(phone, phone_country || DEFAULT_COUNTRY) ?? undefined : undefined,
          channels: send_sms ? ["email", "sms"] : undefined,
        },
      },
      {
        onSuccess: () => {
          setShowParticipant(false);
          // Refetch so the pending invitation appears immediately, rather than
          // the invite seeming to vanish until they accept.
          refetchDeal();
        },
      }
    );
  };

  if (!deal) return <DealDetailSkeleton />;

  const phases = getDealPhases(deal);
  // Prefer the server's figure; fall back to the local computation only while
  // the stats request is still in flight, so the bar never starts at zero.
  // `tasks` is now the *filtered* set, so the local fallback is only honest
  // while nothing is filtered. The server figure is the real source either way;
  // the fallback exists so the bar never flashes zero on first paint.
  // Phase now always scopes the fetch, so the local task set is never the whole
  // deal — only the server figure is honest. Local fallback is first-paint only.
  const isTaskListFiltered = true;
  const pct = dealStats?.task_progress_percent
    ?? (isTaskListFiltered ? 0 : getTaskProgress(tasks).percentage);
  const visibleTasks = tasks;
  // The seller's under-contract checklist leaves position 5 open for whatever
  // extra contingencies this contract carries. Anchoring to the row above it is
  // what puts a new one in that gap instead of at the bottom of the list.
  const contingencyAnchor = tasks.find((task) => task.title === CONTINGENCY_ANCHOR_TITLE);
  const nextPhase = phases[phases.indexOf(deal.phase) + 1];
  const isUnderContract = deal.phase === DealPhase.UNDER_CONTRACT;
  const canAddContingency = isUnderContract && deal?.deal_type === "sell" && Boolean(contingencyAnchor);

  return (
    <div className="min-w-0">
      <div className="overflow-hidden rounded-xl border border-dash-border bg-white p-4 sm:p-6">
        <Link to={PROTECTED_ROUTES.DASHBOARD} className="mb-3 inline-flex items-center gap-1 text-sm text-stone hover:text-moss sm:mb-2" data-testid="back-to-dashboard">
          <ChevronLeft className="w-3.5 h-3.5" /> Back to Deals
        </Link>

        <DealIdentityHeader
          deal={deal}
          needsAttention={deal.snapshot?.health === "at_risk"}
          onEditAddress={canAct ? () => setShowAddress(true) : undefined}
          onChangeCover={canAct ? (url) => saveCover({ dealId: deal.id, payload: { hero_image_url: url } }) : undefined}
        >
          {/* Progress bar + status metrics */}
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
            <div className="h-1.5 w-full overflow-hidden rounded-full bg-edge sm:flex-1">
              <div className="h-full rounded-full bg-dash-rust" style={{ width: `${pct}%` }} />
            </div>
            <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs sm:flex-nowrap sm:whitespace-nowrap">
              <span className="font-semibold text-ink">{pct}% complete</span>
              {!!dealStats?.overdue_tasks && (
                <span className="text-dash-rust font-medium">
                  {dealStats.overdue_tasks} overdue task{dealStats.overdue_tasks === 1 ? "" : "s"}
                </span>
              )}
              {dealStats?.last_activity_at && (
                <span className="text-stone">Last activity {formatRelativeTime(dealStats.last_activity_at)}</span>
              )}
            </div>
            {canAct && nextPhase && (
              <button
                type="button"
                onClick={confirmAdvancePhase}
                disabled={advancing}
                className="inline-flex items-center gap-2 self-start whitespace-nowrap rounded-lg border border-dash-border px-4 py-2 text-sm hover:bg-bone disabled:opacity-60 sm:self-auto"
                data-testid="advance-phase-btn"
              >
                Advance to {PHASE_LABELS[nextPhase]} <ArrowRight className="w-3.5 h-3.5" />
              </button>
            )}
            {/* Client feedback: a deal can be cancelled at any stage, and a
                closed deal can be archived straight from its header. */}
            {canAct && !deal.archived_at && (
              deal.phase === DealPhase.CLOSED ? (
                <button
                  type="button"
                  onClick={() => setEndDeal("archive")}
                  className="inline-flex items-center gap-2 self-start whitespace-nowrap rounded-lg border border-dash-border px-4 py-2 text-sm hover:bg-bone sm:self-auto"
                  data-testid="archive-closed-deal-btn"
                >
                  <Archive className="w-3.5 h-3.5" /> Archive deal
                </button>
              ) : (
                <button
                  type="button"
                  onClick={() => setEndDeal("cancel")}
                  className="inline-flex items-center gap-2 self-start whitespace-nowrap rounded-lg border border-dash-border px-4 py-2 text-sm text-dash-muted hover:bg-bone hover:text-destructive sm:self-auto"
                  data-testid="cancel-deal-btn"
                >
                  <XCircle className="w-3.5 h-3.5" /> Cancel deal
                </button>
              )
            )}
          </div>
        </DealIdentityHeader>

        {/* Status, contract terms and what needs action now live on the
            Overview tab (client items 6 and 11); the stage rail stays above
            the tabs so every tab shows where the deal is. */}
        <div className="mt-5">
          <StagePipeline pipeline={buildStagePipeline({ deal, phases, labels: PHASE_LABELS })} variant="rail" />
        </div>

        {/* The only entry point to the client status update and the
            brokerage's status-template picker (opened from Overview). */}
        {showSnapshotEditor && (
          <SnapshotEditor
            snapshot={deal.snapshot}
            dealId={deal.id}
            onClose={() => setShowSnapshotEditor(false)}
            onSaved={() => { setShowSnapshotEditor(false); refetchDeal(); }}
          />
        )}

        <DealTabs tab={activeTab} items={visibleTabs} onChange={changeTab} badges={{ chat: totalUnread(chatThreads) }} className="mt-4" />
      </div>

        {/* Tab content */}
        <div key={activeTab} className="mt-3 min-w-0 animate-in fade-in slide-in-from-bottom-1 duration-200 sm:mt-2">
          {(() => {
            switch (activeTab) {
              case "overview":
                return (
                  <DealOverview
                    deal={deal}
                    tasks={allTasks}
                    documentsOnFile={dealStats?.documents_on_file ?? 0}
                    canAct={canAct}
                    onOpenTab={changeTab}
                    onOpenTask={editTask}
                    onToggleTask={toggleTask}
                    onAddTask={() => { setTaskForm(createTaskForm(deal.phase)); setShowTask(true); }}
                    onEditProperty={canAct ? () => setShowAddress(true) : undefined}
                    onSavedTerms={syncInspectionToChecklist}
                    onEditStatus={canAct ? () => setShowSnapshotEditor(true) : undefined}
                  />
                );
              case "timeline":
                return (
                  <div className="flex flex-col gap-5">
                  <TimelineTab
                    deal={deal}
                    view={taskView}
                    onViewChange={setTaskView}
                    taskSearch={taskSearch}
                    onSearchChange={setTaskSearch}
                    taskPhase={effectiveTaskPhase}
                    onPhaseChange={setTaskPhase}
                    phaseFilters={taskPhaseFilters(deal)}
                    visibleTasks={visibleTasks}
                    taskCount={dealStats?.tasks_total ?? tasks.length}
                    onToggle={toggleTask}
                    onNudge={(task) => nudgeTask({ dealId: id, taskId: task.id })}
                    onEditTask={canAct ? editTask : null}
                    // A search shows a scattered subset, where "drag above this"
                    // has no clear meaning — reordering waits for a full list.
                    onReorder={canAct && !debouncedTaskSearch ? (taskIds) => reorderTasks({ dealId: id, taskIds }) : null}
                    onReleaseTask={releaseTaskToClient}
                    onConfirmTask={confirmTaskForClient}
                    clientActionTaskId={clientActionTaskId}
                    onAddTask={() => { setTaskForm(createTaskForm(deal.phase)); setShowTask(true); }}
                    onAddContingency={canAddContingency
                      ? () => { setTaskForm(createContingencyForm(contingencyAnchor.id)); setShowTask(true); }
                      : null}
                  />
                  </div>
                );
              case "documents":
                return <DocumentsTab docs={docs} deal={deal} onOpen={openDoc} onAddDocument={openDocumentDialog} onChangeAudience={(doc, audience) => patchDocument({ docId: doc.id, dealId: id, audience })} />;
              case "team":
                return (
                  <TeamTab
                    deal={deal}
                    canAct={canAct}
                    onAddParticipant={() => setShowParticipant(true)}
                    onResendInvite={(participant) =>
                      resendInvitation({ invitationId: participant.invitation_id, payload: {} })}
                    resending={resendingInvite}
                    inviteDisabledReason={billingBlocked ? blockedReason : undefined}
                    onChangeClientContact={(userId, options) =>
                      patchDeal({ dealId: id, payload: { client_contact_user_id: userId } }, options)}
                    savingClientContact={advancing}
                  />
                );
              case "chat":
                return chatViewer ? (
                  <DealChat
                    dealId={id}
                    viewer={chatViewer}
                    socketStatus={chatSocketStatus}
                    onInvite={canAct && !billingBlocked ? () => setShowParticipant(true) : undefined}
                  />
                ) : null;
              case "voice":
                return (
                  <CommunicationTab
                    asks={asks}
                    openAskId={openAskId}
                    onToggleAsk={setOpenAskId}
                    askReply={askReply}
                    onAskReplyChange={setAskReply}
                    onCloseAsk={closeAsk}
                    onSendAskReply={sendAskReply}
                    onConvertToTask={(ask) => {
                      setTaskForm((current) => ({ ...current, title: ask.subject || "", description: ask.body || "" }));
                      setShowTask(true);
                    }}
                    onSendUpdate={() => setShowUpdate(true)}
                  />
                );
              case "schedule":
                return <AppointmentsTab deal={deal} canAct={canAct} canSchedule={canAct && !billingBlocked} />;
              case "share_link":
                return <ShareTab deal={deal} onUpdated={() => refetchDeal()} />;
              case "activity":
                return <ActivityTab activity={activity} deal={deal} />;
              case "ai":
                return <AiTab deal={deal} />;
              default:
                return null;
            }
          })()}
        </div>

      <TaskDialog
        open={showTask}
        onClose={() => setShowTask(false)}
        onSubmit={submitTask}
        defaultValues={taskForm}
        phases={phases.slice(0, 3)}
        submitting={patchingTask || postingTask}
        onDelete={canAct ? removeTask : null}
        onToggleNeeded={canAct ? toggleTaskNeeded : null}
      />
      <DocumentDialog
        open={showDoc}
        onClose={() => { setShowDoc(false); setDocumentRequestedFor(null); }}
        onSubmit={addDoc}
        defaultValues={documentForm}
        roles={ROLE_STYLES}
        requestedFor={documentRequestedFor}
      />
      {documentViewer}
      <UpdateDialog open={showUpdate} onClose={() => setShowUpdate(false)} onSubmit={sendUpdate} busy={sendingUpdate} />
      <ParticipantDialog open={showParticipant} onClose={() => setShowParticipant(false)} onSubmit={addParticipant} defaultValues={createParticipantForm()} roles={ROLE_STYLES} busy={invitingParticipant} />

      <DealAddressDialog
        deal={deal}
        open={showAddress}
        onClose={() => setShowAddress(false)}
        onSaved={() => refetchDeal()}
      />

      {showAdvanceConfirm && (
        <ConfirmDialog
          open={showAdvanceConfirm}
          onOpenChange={setShowAdvanceConfirm}
          tone="default"
          title={`Advance to ${PHASE_LABELS[nextPhase]}?`}
          description="This moves the deal to its next stage. Stages can't be reversed from here — the team, tasks and client view will all update immediately."
          confirmLabel={`Advance to ${PHASE_LABELS[nextPhase]}`}
          onConfirm={() => { setShowAdvanceConfirm(false); advancePhase(); }}
          testId="advance-phase-confirm-dialog"
        />
      )}

      {endDeal && (
        <ConfirmDialog
          open
          onOpenChange={(open) => { if (!open) setEndDeal(null); }}
          tone={endDeal === "cancel" ? "destructive" : "default"}
          title={endDeal === "cancel" ? "Cancel this deal?" : "Archive this closed deal?"}
          description={endDeal === "cancel"
            ? "The deal is marked cancelled and moved to Archived. It leaves your board and stops counting toward your plan's active-deal limit. All history is kept, and you can restore it from Archived at any time."
            : "It moves to Archived and leaves your board. All history is kept, and you can restore it at any time."}
          confirmLabel={endDeal === "cancel" ? "Cancel deal" : "Archive deal"}
          busyLabel={endDeal === "cancel" ? "Cancelling…" : "Archiving…"}
          onConfirm={async () => {
            if (endDeal === "cancel") await cancelDeal(id);
            else await archiveDeal(id);
            setEndDeal(null);
            navigate("/dashboard");
          }}
          testId="end-deal-dialog"
        />
      )}

      {showContractTerms && (
        <ContractTermsPrompt
          deal={deal}
          onClose={() => setShowContractTerms(false)}
          onSaved={syncInspectionToChecklist}
        />
      )}

      {scheduleTask && (
        <ScheduleTaskModal
          task={scheduleTask}
          onSaved={saveScheduled}
          onSkip={skipScheduled}
          onCancel={() => setScheduleTask(null)}
        />
      )}
    </div>
  );
}
